/**
 * Enhanced Group Block JavaScript
 * Provides advanced functionality for the enhanced group.liquid block
 */

class EnhancedGroupBlock {
  constructor(element) {
    this.element = element;
    this.settings = this.parseSettings();
    this.init();
  }

  parseSettings() {
    const data = this.element.dataset;
    return {
      // Layout settings
      direction: data.groupDirection || 'column',
      mobileDirection: data.groupMobileDirection || 'column',
      alignment: data.groupAlignment || 'flex-start',
      mobileAlignment: data.groupMobileAlignment || 'inherit',
      justifyContent: data.groupJustifyContent || 'center',
      mobileJustifyContent: data.groupMobileJustifyContent || 'center',
      gap: data.groupGap || 12,
      mobileGap: data.groupMobileGap || 8,
      
      // Sizing settings
      width: data.groupWidth || '100%',
      mobileWidth: data.groupMobileWidth || '100%',
      height: data.groupHeight || 'auto',
      mobileHeight: data.groupMobileHeight || 'auto',
      
      // Typography settings
      fontSize: data.groupFontSize || 16,
      mobileFontSize: data.groupMobileFontSize || 14,
      lineHeight: data.groupLineHeight || 1.5,
      letterSpacing: data.groupLetterSpacing || 0,
      fontWeight: data.groupFontWeight || 400,
      textAlign: data.groupTextAlign || 'left',
      mobileTextAlign: data.groupMobileTextAlign || 'left',
      
      // Background settings
      backgroundMedia: data.groupBackgroundMedia || 'none',
      backgroundImage: data.groupBackgroundImage || '',
      backgroundVideo: data.groupBackgroundVideo || '',
      backgroundColor: data.groupBackgroundColor || 'transparent',
      gradientStart: data.groupGradientStart || '#ffffff',
      gradientEnd: data.groupGradientEnd || '#000000',
      gradientDirection: data.groupGradientDirection || 'to bottom',
      
      // Overlay settings
      overlayEnabled: data.groupOverlayEnabled === 'true',
      overlayColor: data.groupOverlayColor || 'rgba(0, 0, 0, 0.1)',
      overlayOpacity: data.groupOverlayOpacity || 20,
      
      // Border settings
      borderStyle: data.groupBorderStyle || 'none',
      borderWidth: data.groupBorderWidth || 1,
      borderColor: data.groupBorderColor || '#000000',
      borderRadius: data.groupBorderRadius || 0,
      mobileBorderRadius: data.groupMobileBorderRadius || 0,
      
      // Padding settings
      padding: {
        top: data.groupPaddingTop || 0,
        bottom: data.groupPaddingBottom || 0,
        left: data.groupPaddingLeft || 0,
        right: data.groupPaddingRight || 0
      },
      mobilePadding: {
        top: data.groupMobilePaddingTop || 0,
        bottom: data.groupMobilePaddingBottom || 0,
        left: data.groupMobilePaddingLeft || 0,
        right: data.groupMobilePaddingRight || 0
      },
      
      // Animation settings
      animationType: data.groupAnimationType || 'none',
      animationDuration: data.groupAnimationDuration || 0.5,
      animationDelay: data.groupAnimationDelay || 0,
      
      // Advanced settings
      displayType: data.groupDisplayType || 'flex',
      customClass: data.groupCustomClass || '',
      customId: data.groupCustomId || '',
      
      // Link settings
      link: data.groupLink || '',
      openInNewTab: data.groupOpenInNewTab === 'true',
      linkTitle: data.groupLinkTitle || ''
    };
  }

  init() {
    this.applyStyles();
    this.setupEventListeners();
    this.initializeAnimations();
    this.setupResponsiveHandlers();
    this.setupAccessibility();
  }

  applyStyles() {
    // Apply CSS custom properties
    const styles = {
      // Layout
      '--group-direction': this.settings.direction,
      '--group-mobile-direction': this.settings.mobileDirection,
      '--group-align': this.settings.alignment,
      '--group-mobile-align': this.settings.mobileAlignment,
      '--group-justify': this.settings.justifyContent,
      '--group-mobile-justify': this.settings.mobileJustifyContent,
      '--group-gap': `${this.settings.gap}px`,
      '--group-mobile-gap': `${this.settings.mobileGap}px`,
      
      // Sizing
      '--group-width': this.settings.width,
      '--group-mobile-width': this.settings.mobileWidth,
      '--group-height': this.settings.height,
      '--group-mobile-height': this.settings.mobileHeight,
      
      // Typography
      '--group-font-size': `${this.settings.fontSize}px`,
      '--group-mobile-font-size': `${this.settings.mobileFontSize}px`,
      '--group-line-height': this.settings.lineHeight,
      '--group-letter-spacing': `${this.settings.letterSpacing}px`,
      '--group-font-weight': this.settings.fontWeight,
      '--group-text-align': this.settings.textAlign,
      '--group-mobile-text-align': this.settings.mobileTextAlign,
      
      // Background
      '--group-background': this.getBackgroundValue(),
      '--group-bg-image': this.settings.backgroundImage ? `url(${this.settings.backgroundImage})` : 'none',
      '--group-bg-size': 'cover',
      '--group-mobile-bg-size': 'cover',
      '--group-gradient-direction': this.settings.gradientDirection,
      '--group-gradient-start': this.settings.gradientStart,
      '--group-gradient-end': this.settings.gradientEnd,
      
      // Overlay
      '--group-overlay-color': this.settings.overlayColor,
      '--group-overlay-opacity': `${this.settings.overlayOpacity}%`,
      
      // Border
      '--group-border': this.getBorderValue(),
      '--group-border-color': this.settings.borderColor,
      '--group-border-radius': `${this.settings.borderRadius}px`,
      '--group-mobile-border-radius': `${this.settings.mobileBorderRadius}px`,
      
      // Padding
      '--group-padding': `${this.settings.padding.top}px ${this.settings.padding.right}px ${this.settings.padding.bottom}px ${this.settings.padding.left}px`,
      '--group-mobile-padding': `${this.settings.mobilePadding.top}px ${this.settings.mobilePadding.right}px ${this.settings.mobilePadding.bottom}px ${this.settings.mobilePadding.left}px`,
      
      // Animation
      '--group-animation-duration': `${this.settings.animationDuration}s`,
      '--group-animation-delay': `${this.settings.animationDelay}s`
    };

    Object.assign(this.element.style, styles);

    // Add CSS classes
    this.element.classList.add('group-block');
    
    if (this.settings.customClass) {
      this.element.classList.add(...this.settings.customClass.split(' '));
    }
    
    if (this.settings.customId) {
      this.element.id = this.settings.customId;
    }

    // Add background media classes
    if (this.settings.backgroundMedia !== 'none') {
      this.element.classList.add(`group-block--background-${this.settings.backgroundMedia}`);
    }

    // Add display type class
    this.element.classList.add(`group-block--display-${this.settings.displayType}`);

    // Add border style class
    if (this.settings.borderStyle !== 'none') {
      this.element.classList.add(`group-block--border-${this.settings.borderStyle}`);
    }

    // Add overlay class
    if (this.settings.overlayEnabled) {
      this.element.classList.add('has-overlay');
    }

    // Add animation class
    if (this.settings.animationType !== 'none') {
      this.element.classList.add(`group-block--animate-${this.settings.animationType}`);
    }
  }

  getBackgroundValue() {
    switch (this.settings.backgroundMedia) {
      case 'color':
        return this.settings.backgroundColor;
      case 'gradient':
        return `linear-gradient(${this.settings.gradientDirection}, ${this.settings.gradientStart}, ${this.settings.gradientEnd})`;
      case 'image':
        return `url(${this.settings.backgroundImage})`;
      default:
        return 'transparent';
    }
  }

  getBorderValue() {
    if (this.settings.borderStyle === 'none') {
      return 'none';
    }
    return `${this.settings.borderWidth}px ${this.settings.borderStyle} ${this.settings.borderColor}`;
  }

  setupEventListeners() {
    // Link functionality
    if (this.settings.link) {
      this.element.style.cursor = 'pointer';
      this.element.addEventListener('click', this.handleClick.bind(this));
      this.element.addEventListener('keydown', this.handleKeydown.bind(this));
      
      // Make it focusable for accessibility
      this.element.setAttribute('tabindex', '0');
      this.element.setAttribute('role', 'button');
      
      if (this.settings.linkTitle) {
        this.element.setAttribute('title', this.settings.linkTitle);
      }
    }

    // Intersection Observer for animations
    if (this.settings.animationType !== 'none') {
      this.setupIntersectionObserver();
    }

    // Background video handling
    if (this.settings.backgroundVideo) {
      this.setupBackgroundVideo();
    }
  }

  handleClick(event) {
    if (this.settings.link) {
      event.preventDefault();
      
      if (this.settings.openInNewTab) {
        window.open(this.settings.link, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = this.settings.link;
      }
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleClick(event);
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.element.classList.add('group-block--animate-visible');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px'
    });

    observer.observe(this.element);
  }

  setupBackgroundVideo() {
    const video = this.element.querySelector('video');
    if (video) {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      
      // Play video when it becomes visible
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            video.play().catch(() => {
              // Handle autoplay restrictions
              console.log('Video autoplay was prevented');
            });
          } else {
            video.pause();
          }
        });
      });
      
      videoObserver.observe(video);
    }
  }

  initializeAnimations() {
    // Add animation delay via CSS custom property
    if (this.settings.animationType !== 'none') {
      this.element.style.setProperty('--group-animation-delay', `${this.settings.animationDelay}s`);
    }
  }

  setupResponsiveHandlers() {
    // Handle responsive sizing
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
      const isDesktop = window.innerWidth > 1024;

      // Apply responsive styles
      if (isMobile) {
        this.element.classList.add('group-block--mobile');
        this.element.classList.remove('group-block--tablet', 'group-block--desktop');
      } else if (isTablet) {
        this.element.classList.add('group-block--tablet');
        this.element.classList.remove('group-block--mobile', 'group-block--desktop');
      } else {
        this.element.classList.add('group-block--desktop');
        this.element.classList.remove('group-block--mobile', 'group-block--tablet');
      }
    };

    // Initial call
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
  }

  setupAccessibility() {
    // Add ARIA attributes
    if (this.settings.link) {
      this.element.setAttribute('role', 'button');
      this.element.setAttribute('tabindex', '0');
      
      if (this.settings.linkTitle) {
        this.element.setAttribute('aria-label', this.settings.linkTitle);
      }
    }

    // Ensure proper focus management
    this.element.addEventListener('focus', () => {
      this.element.classList.add('group-block--focused');
    });

    this.element.addEventListener('blur', () => {
      this.element.classList.remove('group-block--focused');
    });

    // Add keyboard navigation support
    this.element.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        this.element.classList.add('group-block--keyboard-navigation');
      }
    });

    this.element.addEventListener('mousedown', () => {
      this.element.classList.remove('group-block--keyboard-navigation');
    });
  }

  // Public methods for dynamic updates
  updateSetting(key, value) {
    this.settings[key] = value;
    this.applyStyles();
  }

  updateStyles(styles) {
    Object.assign(this.element.style, styles);
  }

  addClass(className) {
    this.element.classList.add(className);
  }

  removeClass(className) {
    this.element.classList.remove(className);
  }

  toggleClass(className) {
    this.element.classList.toggle(className);
  }

  // Animation control methods
  playAnimation() {
    if (this.settings.animationType !== 'none') {
      this.element.classList.add(`group-block--animate-${this.settings.animationType}`);
    }
  }

  pauseAnimation() {
    if (this.settings.animationType !== 'none') {
      this.element.classList.remove(`group-block--animate-${this.settings.animationType}`);
    }
  }

  // Utility methods
  getDimensions() {
    return this.element.getBoundingClientRect();
  }

  isInViewport() {
    const rect = this.getDimensions();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  destroy() {
    // Remove event listeners
    this.element.removeEventListener('click', this.handleClick);
    this.element.removeEventListener('keydown', this.handleKeydown);
    
    // Remove classes
    this.element.className = this.element.className.replace(/group-block--\S+/g, '');
    
    // Clear styles
    this.element.style.cssText = '';
    
    // Remove attributes
    this.element.removeAttribute('style');
    this.element.removeAttribute('role');
    this.element.removeAttribute('tabindex');
    this.element.removeAttribute('title');
  }
}

// Initialize Enhanced Group Blocks
document.addEventListener('DOMContentLoaded', () => {
  const groupBlocks = document.querySelectorAll('[data-group-block]');
  
  groupBlocks.forEach(element => {
    new EnhancedGroupBlock(element);
  });
});

// Auto-initialize new elements added to the DOM
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) { // Element node
        if (node.matches('[data-group-block]')) {
          new EnhancedGroupBlock(node);
        }
        
        // Check for nested elements
        const nestedBlocks = node.querySelectorAll('[data-group-block]');
        nestedBlocks.forEach(element => {
          new EnhancedGroupBlock(element);
        });
      }
    });
  });
});

// Start observing the document
observer.observe(document.body, {
  childList: true,
  subtree: true
});

// Export for use in other scripts
window.EnhancedGroupBlock = EnhancedGroupBlock;

// Utility functions
window.GroupBlockUtils = {
  // Get all group blocks
  getAll() {
    return document.querySelectorAll('.group-block');
  },
  
  // Get group block by ID
  getById(id) {
    return document.getElementById(id);
  },
  
  // Get group blocks by class
  getByClass(className) {
    return document.querySelectorAll(`.${className}`);
  },
  
  // Update all group blocks
  updateAll(callback) {
    const blocks = this.getAll();
    blocks.forEach(callback);
  },
  
  // Refresh all group blocks
  refresh() {
    const blocks = document.querySelectorAll('[data-group-block]');
    blocks.forEach(element => {
      if (element.groupBlockInstance) {
        element.groupBlockInstance.applyStyles();
      }
    });
  }
};